/**
 * Ticket-based temporary access system
 *
 * Tickets are simple codes stored in the database.
 * Each device gets its own 1 week access window starting from first scan.
 * Same QR code works for multiple devices - each gets their own timer.
 */

import { supabase } from '~/supabase-client'

// Duration in milliseconds (1 week)
const TICKET_DURATION_MS = 7 * 24 * 60 * 60 * 1000

const DEMO_CREDENTIALS = {
  email: import.meta.env.DEMO_USER_EMAIL || 'bmilligan9+admin@outlook.com',
  password: import.meta.env.DEMO_USER_PASSWORD || 'password123'
}

/**
 * Generate a simple device fingerprint
 * Not cryptographically secure, but sufficient for demo access tracking
 */
function getDeviceId() {
  // Check localStorage first for existing device ID
  const stored = localStorage.getItem('terra_device_id')
  if (stored) return stored

  // Generate new device ID from browser characteristics
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.platform
  ]

  // Simple hash function
  const fingerprint = components.join('|')
  let hash = 0
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }

  const deviceId = 'dev_' + Math.abs(hash).toString(36) + '_' + Date.now().toString(36)

  // Store for consistency across page loads
  localStorage.setItem('terra_device_id', deviceId)
  return deviceId
}

/**
 * Validate a ticket for this device and return credentials if valid
 * On first use from this device, creates an activation (starts the 24-hour timer)
 *
 * @param {string} ticketId - The ticket code from URL
 * @returns {Promise<{valid: boolean, credentials?: object, error?: string, expiresAt?: Date}>}
 */
export async function validateTicket(ticketId) {
  if (!ticketId) {
    return { valid: false, error: 'No ticket provided' }
  }

  try {
    const deviceId = getDeviceId()

    // Check if ticket exists
    const { data: ticket, error: ticketError } = await supabase
      .from('demo_tickets')
      .select('id')
      .eq('id', ticketId)
      .single()

    if (ticketError || !ticket) {
      return { valid: false, error: 'Invalid ticket' }
    }

    // Check for existing activation for this device
    const { data: activation } = await supabase
      .from('demo_ticket_activations')
      .select('*')
      .eq('ticket_id', ticketId)
      .eq('device_id', deviceId)
      .single()

    const now = new Date()

    if (activation) {
      // Device already activated - check expiry
      const expiresAt = new Date(activation.expires_at)
      if (now > expiresAt) {
        return {
          valid: false,
          error: 'Ticket has expired',
          expiresAt
        }
      }

      // Still valid
      return {
        valid: true,
        credentials: DEMO_CREDENTIALS,
        expiresAt
      }
    }

    // No activation for this device - create one
    const expiresAt = new Date(now.getTime() + TICKET_DURATION_MS)

    const { error: insertError } = await supabase
      .from('demo_ticket_activations')
      .insert({
        ticket_id: ticketId,
        device_id: deviceId,
        expires_at: expiresAt.toISOString()
      })

    if (insertError) {
      console.error('Failed to create activation:', insertError)
      // Still allow login even if tracking fails
    }

    return {
      valid: true,
      credentials: DEMO_CREDENTIALS,
      expiresAt
    }
  } catch (e) {
    console.error('Ticket validation error:', e)
    return { valid: false, error: 'Ticket validation failed' }
  }
}
